import { useState } from 'react'

import { t } from '../../i18n/lingua'

/**
 * The image of an «Immagine» widget: a file from the computer, resized in
 * the browser (the longer side at most 1600 px, re-encoded, so no EXIF and
 * no GPS leave the computer) and kept in the dashboard as a data URL, or an
 * address. The dashboard travels with the picture: no upload, no storage,
 * nothing to clean up when the dashboard goes.
 */
const LATO_MASSIMO = 1600
/** The longest data URL a dashboard carries for one picture (the server refuses more). */
const LUNGHEZZA_MASSIMA = 700_000

async function ridimensiona(file: File): Promise<string> {
  const url = URL.createObjectURL(file)
  try {
    const img = await new Promise<HTMLImageElement>((ok, no) => {
      const i = new Image()
      i.onload = () => ok(i)
      i.onerror = () => no(new Error('immagine non leggibile'))
      i.src = url
    })
    const png = file.type === 'image/png' || file.type === 'image/svg+xml'
    let lato = LATO_MASSIMO
    for (let tentativo = 0; tentativo < 5; tentativo += 1) {
      const scala = Math.min(1, lato / Math.max(img.naturalWidth, img.naturalHeight))
      const canvas = document.createElement('canvas')
      canvas.width = Math.max(1, Math.round(img.naturalWidth * scala))
      canvas.height = Math.max(1, Math.round(img.naturalHeight * scala))
      const ctx = canvas.getContext('2d')
      if (!ctx) throw new Error('canvas')
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height)
      const dati = png ? canvas.toDataURL('image/png') : canvas.toDataURL('image/jpeg', 0.85)
      if (dati.length <= LUNGHEZZA_MASSIMA) return dati
      lato = Math.round(lato * 0.7)
    }
    throw new Error(t('L’immagine resta troppo grande anche ridotta: usa un file più semplice o un indirizzo.'))
  } finally {
    URL.revokeObjectURL(url)
  }
}

export function SceltaImmagine({ src, onCambia }: { src: string; onCambia: (src: string) => void }) {
  const [busy, setBusy] = useState(false)
  const [errore, setErrore] = useState<string | null>(null)
  const [indirizzo, setIndirizzo] = useState(src.startsWith('data:') ? '' : src)
  const daFile = src.startsWith('data:')

  async function carica(file: File) {
    setBusy(true)
    setErrore(null)
    try {
      onCambia(await ridimensiona(file))
    } catch (e) {
      setErrore(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="field span-6">
      <span className="field__label">{t('Immagine')}</span>
      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        {src && <img src={src} alt="" style={{ maxWidth: 160, maxHeight: 100, borderRadius: 6, border: '1px solid var(--divider)' }} />}
        <div style={{ display: 'grid', gap: 6, flex: 1, minWidth: 220 }}>
          <label className="secondary" role="button" style={{ display: 'inline-block', width: 'fit-content' }}>
            {busy ? t('Carico…') : daFile ? t('Cambia file…') : t('Scegli un file…')}
            <input type="file" accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml" hidden disabled={busy}
                   onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) void carica(f) }} />
          </label>
          <div style={{ display: 'flex', gap: 6 }}>
            <input type="url" value={indirizzo} placeholder="https://…/planimetria.png" onChange={(e) => setIndirizzo(e.target.value)} style={{ flex: 1 }} />
            <button type="button" className="secondary" onClick={() => onCambia(indirizzo.trim())} disabled={!indirizzo.trim()}>{t('Usa l’indirizzo')}</button>
          </div>
          {src && <button type="button" className="linklike" style={{ width: 'fit-content' }} onClick={() => { onCambia(''); setIndirizzo('') }}>{t('Togli l’immagine')}</button>}
          <span className="field__hint">
            {daFile
              ? t('Il file viene ridotto nel browser (lato lungo 1600 px) e salvato dentro la dashboard: {kb} kB.', { kb: Math.round(src.length * 0.75 / 1024) })
              : t('Un file dal computer viene ridotto nel browser e salvato dentro la dashboard; un indirizzo deve essere raggiungibile dal monitor.')}
          </span>
          {errore && <span className="eagle-motivo">{errore}</span>}
        </div>
      </div>
    </div>
  )
}
